import { Chat, PinnedChat } from '@src/api/shared/community/chatApiClient';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import { SHARED_URL } from '@src/routes/shared';
import { SANITIZE_CONFIG } from '@src/utils/sanitizeConfig';
import DOMPurify from 'dompurify';
import { generatePath } from 'react-router-dom';

export const generateHtml = (inputHtml: string, isEdited: boolean): string => {
  const { ALLOWED_TAGS, ALLOWED_ATTR } = SANITIZE_CONFIG.CHAT;
  const sanitizedHtml = DOMPurify.sanitize(inputHtml, {
    ALLOWED_TAGS: [...ALLOWED_TAGS],
    ALLOWED_ATTR: [...ALLOWED_ATTR],
  });
  const domParser = new DOMParser();
  const parsedDocument = domParser.parseFromString(sanitizedHtml, 'text/html');
  const bodyElement = parsedDocument.body;

  bodyElement.querySelectorAll('a').forEach((anchorElement) => {
    const existingClass = anchorElement.getAttribute('class') || '';
    const classNames = existingClass.split(/\s+/).filter(Boolean);
    const requiredClasses = ['text-primary', 'underline', 'hover:no-underline'];
    const mergedClasses = Array.from(new Set([...classNames, ...requiredClasses]));
    anchorElement.setAttribute('class', mergedClasses.join(' '));
    anchorElement.setAttribute('target', '_blank');
    anchorElement.setAttribute('rel', 'noopener noreferrer');
  });

  const urlRegex = /https?:\/\/[^\s"'<>]+(?=$|[\s"'<>])/gi;
  const textNodeWalker = parsedDocument.createTreeWalker(bodyElement, NodeFilter.SHOW_TEXT, null);
  let currentNode: Node | null;
  while ((currentNode = textNodeWalker.nextNode())) {
    const textNode = currentNode as Text;
    const textContent = textNode.nodeValue || '';

    if (!urlRegex.test(textContent)) {
      continue;
    }
    if (textNode.parentElement?.tagName === 'A' || textNode.parentElement?.tagName === 'P') {
      continue;
    }

    const documentFragment = parsedDocument.createDocumentFragment();
    let lastProcessedIndex = 0;

    textContent.replace(urlRegex, (matchedUrl, matchedOffset) => {
      const cleanedUrl = matchedUrl.replace(/['"”’)\]!?,.]+$/, '');
      if (matchedOffset > lastProcessedIndex) {
        const textSegment = textContent.slice(lastProcessedIndex, matchedOffset);
        documentFragment.appendChild(parsedDocument.createTextNode(textSegment));
      }

      const newAnchor = parsedDocument.createElement('a');
      newAnchor.href = cleanedUrl;
      newAnchor.textContent = cleanedUrl;
      newAnchor.setAttribute('target', '_blank');
      newAnchor.setAttribute('rel', 'noopener noreferrer');
      newAnchor.setAttribute('class', 'text-primary underline hover:no-underline');
      documentFragment.appendChild(newAnchor);
      lastProcessedIndex = matchedOffset + matchedUrl.length;
      return matchedUrl;
    });

    if (lastProcessedIndex < textContent.length) {
      const remainingText = textContent.slice(lastProcessedIndex);
      documentFragment.appendChild(parsedDocument.createTextNode(remainingText));
    }

    textNode.parentNode!.replaceChild(documentFragment, textNode);
  }
  const result = bodyElement.innerHTML.replace(
    /<p>([\s\S]*?)<\/p>/gi,
    (_match, paragraphContent) => {
      const contentOnly = paragraphContent.replace(/<br[^>]*>/gi, '').trim();
      return `${contentOnly ? paragraphContent : ''}<br>`;
    },
  );

  return isEdited ? appendEditedMark(result) : result;
};

export const appendEditedMark = (inputHtml: string): string => {
  const baseHtml = inputHtml;

  const parser = new DOMParser();
  const doc = parser.parseFromString(baseHtml, 'text/html');
  const body = doc.body;

  const editedMark = doc.createElement('span');
  editedMark.className = 'text-sm text-gray-400';
  editedMark.textContent = '（編集済み）';

  const last = body.lastChild;

  // 改行で終わっている文章が二重改行にならない対応
  if (last?.nodeName.toLowerCase() === 'br') {
    body.insertBefore(editedMark, last);
  } else {
    body.appendChild(editedMark);
  }

  return body.innerHTML;
};

export const getCommunityMemberDetailUrl = (
  channelCode: string,
  chat: Chat | PinnedChat,
  channelVisibility?: number,
): string | null => {
  if (!channelVisibility || !chat.can_click) {
    return null;
  }

  const url =
    chat.user_type === ChannelMemberUserTypeEnum.TENANT_USER
      ? SHARED_URL.COMMUNITY_CHANNEL_MEMBERS_DETAIL_TENANT
      : SHARED_URL.COMMUNITY_CHANNEL_MEMBERS_DETAIL_COMPANY;

  return generatePath(url, {
    code: channelCode,
    id: chat.user_id,
  });
};

/**
 * 判定対象のHTMLが、実質的に空（<br>タグのみ、または空白文字のみ）かどうかをチェックする
 *
 * @example
 * ```ts
 * isHtmlEmpty("<br>") // true
 * isHtmlEmpty("<br><span>（編集済み）</span>	") // false
 */
export const isHtmlEmpty = (html: string = ''): boolean => {
  const stripped = html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/\s+/g, '')
    .trim();

  return stripped === '';
};
